/**
 * Read every row a PostgREST query matches, a page at a time.
 *
 * Hosted Supabase caps every PostgREST read at "Max rows" (Settings → API, 1000 for this
 * project). An un-ranged select past that returns the first 1000 rows with no error and
 * no hint, so a list that only ever grows (transactions, receipts, matches, invoices)
 * silently loses its tail. Ranged pages under a deterministic order are the only way to
 * read all of it.
 *
 * Deliberately free of any Supabase import: the builder is typed structurally, so the
 * paging rules are tested against a fake (src/lib/__tests__/fetchAllRows.test.ts).
 */

/**
 * The project's PostgREST "Max rows" setting. Must equal it: a page that comes back short
 * is taken as the last page, so if the setting were ever lowered below this, every capped
 * page would look like the end and the truncation would be silent again.
 */
export const SUPABASE_MAX_ROWS = 1000;

/**
 * Ids per `.in()` lookup. About 37 URL bytes per UUID; gateways reject 8–16 KB URLs long
 * before the row cap matters.
 */
export const IN_LIST_CHUNK = 100;

/** How many `.in()` chunks run at once. */
const DEFAULT_CONCURRENCY = 4;

/** Past this many rows, fail rather than quietly hold a huge list in memory. */
const DEFAULT_MAX_ROWS = 100_000;

export interface PostgrestErrorLike {
  message: string;
  code?: string;
  details?: string | null;
  hint?: string | null;
}

export interface OrderKey {
  column: string;
  ascending: boolean;
  nullsFirst?: boolean;
}

export interface RowsPage<Row> {
  data: Row[] | null;
  error: PostgrestErrorLike | null;
}

/** The slice of a supabase-js filter builder the helper needs. */
export interface PageableQuery<Row> extends PromiseLike<RowsPage<Row>> {
  order(
    column: string,
    options?: { ascending?: boolean; nullsFirst?: boolean }
  ): PageableQuery<Row>;
  range(from: number, to: number): PageableQuery<Row>;
}

export interface FetchAllRowsOptions {
  /**
   * Most significant key first. Required: offset paging over an unordered (heap-order)
   * result can skip or repeat rows between pages. Don't also call `.order()` inside
   * `buildQuery` — the helper applies these keys itself.
   */
  order: OrderKey[];
  /** Unique column appended as the last key when absent, so ties cannot shuffle. Default "id". */
  tiebreaker?: string;
  /** Default and hard maximum: SUPABASE_MAX_ROWS. */
  pageSize?: number;
  /** Throw instead of returning a partial list past this many rows. Default 100 000. */
  maxRows?: number;
  /** React Query's signal: checked between pages. */
  signal?: AbortSignal;
}

/**
 * Fetch every page of a query and return the rows in order.
 *
 * `buildQuery` must return a FRESH builder on each call: supabase-js builders are
 * single-use, and each page adds its own `.order()` and `.range()`.
 *
 * Same contract as the hooks: a failed page throws the PostgrestError object (which is
 * not an `Error` instance — use `errorMessage()` to show it), and no partial list is
 * ever returned.
 *
 * Offset paging is not a snapshot: an insert or delete landing between two page reads
 * (daily sync, an import) can repeat or skip one row at a page boundary until the next
 * refetch. Rows are not deduplicated here, because a narrow select may not carry `id`.
 */
export async function fetchAllRows<Row>(
  buildQuery: () => PageableQuery<Row>,
  options: FetchAllRowsOptions
): Promise<Row[]> {
  const pageSize = options.pageSize ?? SUPABASE_MAX_ROWS;
  if (!Number.isInteger(pageSize) || pageSize < 1 || pageSize > SUPABASE_MAX_ROWS) {
    // A page larger than the cap comes back capped, i.e. short, and would read as the end.
    throw new RangeError(
      `pageSize must be 1..${SUPABASE_MAX_ROWS}: a capped full page would look like the last page`
    );
  }
  if (options.order.length === 0) {
    throw new Error("fetchAllRows needs a deterministic order");
  }

  const tiebreaker = options.tiebreaker ?? "id";
  const lastKey = options.order[options.order.length - 1];
  const keys = options.order.some((k) => k.column === tiebreaker)
    ? options.order
    : [...options.order, { column: tiebreaker, ascending: lastKey.ascending }];
  const maxRows = options.maxRows ?? DEFAULT_MAX_ROWS;

  const rows: Row[] = [];
  for (let from = 0; ; from += pageSize) {
    if (options.signal?.aborted) {
      throw options.signal.reason ?? new DOMException("Aborted", "AbortError");
    }

    let query = buildQuery();
    for (const key of keys) {
      query = query.order(
        key.column,
        key.nullsFirst === undefined
          ? { ascending: key.ascending }
          : { ascending: key.ascending, nullsFirst: key.nullsFirst }
      );
    }

    const { data, error } = await query.range(from, from + pageSize - 1);
    if (error) throw error;

    const page = data ?? [];
    if (page.length > pageSize) throw new Error("fetchAllRows: range() not applied");
    for (const row of page) rows.push(row);
    if (rows.length > maxRows) {
      throw new Error(`fetchAllRows: more than ${maxRows} rows, narrow the query`);
    }
    // A short page (including an empty one) is the last.
    if (page.length < pageSize) return rows;
  }
}

/**
 * Run an `.in()` lookup over many ids in chunks and concatenate the results.
 *
 * Each chunk is a single un-ranged request, so it must stay under the row cap: a chunk
 * that comes back with SUPABASE_MAX_ROWS rows may have been cut, and throws rather than
 * returning a list that could be missing rows. No ids means no request at all.
 */
export async function fetchInChunks<Row, Id>(
  ids: readonly Id[],
  buildQuery: (chunk: Id[]) => PromiseLike<RowsPage<Row>>,
  options: { chunkSize?: number; concurrency?: number } = {}
): Promise<Row[]> {
  const chunkSize = options.chunkSize ?? IN_LIST_CHUNK;
  const concurrency = options.concurrency ?? DEFAULT_CONCURRENCY;
  if (!Number.isInteger(chunkSize) || chunkSize < 1) {
    throw new RangeError("chunkSize must be a positive integer");
  }
  if (!Number.isInteger(concurrency) || concurrency < 1) {
    throw new RangeError("concurrency must be a positive integer");
  }

  const chunks: Id[][] = [];
  for (let i = 0; i < ids.length; i += chunkSize) chunks.push(ids.slice(i, i + chunkSize));
  if (chunks.length === 0) return [];

  const results: Row[][] = new Array(chunks.length);
  let next = 0;
  let failed = false;

  // A small worker pool: each worker takes the next chunk until none are left or one
  // has failed. Promise.all rejects with the first failure.
  const worker = async () => {
    while (!failed && next < chunks.length) {
      const index = next++;
      const { data, error } = await buildQuery(chunks[index]);
      if (error) {
        failed = true;
        throw error;
      }
      const rows = data ?? [];
      if (rows.length >= SUPABASE_MAX_ROWS) {
        failed = true;
        throw new Error(
          `fetchInChunks: chunk ${index + 1} hit the server cap of ${SUPABASE_MAX_ROWS} rows, use a smaller chunkSize`
        );
      }
      results[index] = rows;
    }
  };

  await Promise.all(Array.from({ length: Math.min(concurrency, chunks.length) }, worker));
  return results.flat();
}

/**
 * Readable text for whatever a fetch threw: PostgREST errors arrive as plain objects with
 * a `message`, not as `Error` instances.
 */
export function errorMessage(error: unknown): string {
  if (error && typeof error === "object" && "message" in error) {
    const message = (error as { message: unknown }).message;
    if (typeof message === "string" && message) return message;
  }
  return String(error);
}
